import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

/// Отступ меню от кнопки и от края окна.
const MENU_GAP = 6
const VIEWPORT_MARGIN = 8

/// Выпадающее меню — замена PopupMenuButton.
///
/// Закрывается по клику вне, Esc и по выбору пункта; ничего сложнее админке
/// не требовалось, поэтому обходимся без библиотеки поповеров.
export function Menu({
  trigger,
  children,
  align = 'end',
  className,
}: {
  trigger: (props: { open: boolean }) => ReactNode
  children: (props: { close: () => void }) => ReactNode
  align?: 'start' | 'end'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [side, setSide] = useState<'bottom' | 'top'>('bottom')

  /// Меню раскрывается вниз, пока внизу есть место. Кнопка пользователя стоит
  /// у нижнего края сайдбара — оттуда список уезжал за экран целиком. Считаем
  /// после отрисовки: высота зависит от содержимого, заранее её не знать.
  useLayoutEffect(() => {
    if (!open) {
      setSide('bottom')
      return
    }

    const trigger = rootRef.current?.getBoundingClientRect()
    const menu = menuRef.current?.getBoundingClientRect()
    if (!trigger || !menu) return

    const needed = menu.height + MENU_GAP + VIEWPORT_MARGIN
    const below = window.innerHeight - trigger.bottom
    const above = trigger.top
    // Вверх уходим, только если там места действительно больше: иначе меню
    // упрётся в шапку и обрежется с другой стороны.
    setSide(below < needed && above > below ? 'top' : 'bottom')
  }, [open])

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex max-w-full items-center rounded-lg"
      >
        {trigger({ open })}
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className={cn(
            'absolute z-50 min-w-56 overflow-hidden rounded-xl bg-surface py-1.5 shadow-pop',
            // Раскрывается из своего угла — направление подсказывает, чья это
            // кнопка, когда на экране их несколько подряд.
            'animate-pop-in',
            side === 'top' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]',
            align === 'end' ? 'right-0' : 'left-0',
            side === 'top'
              ? align === 'end'
                ? 'origin-bottom-right'
                : 'origin-bottom-left'
              : align === 'end'
                ? 'origin-top-right'
                : 'origin-top-left',
            className,
          )}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      ) : null}
    </div>
  )
}

export function MenuItem({
  children,
  onClick,
  icon,
  tone = 'default',
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  icon?: ReactNode
  tone?: 'default' | 'danger'
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:text-ink-disabled',
        tone === 'danger' ? 'text-danger hover:bg-danger/8' : 'text-ink hover:bg-surface-alt',
      )}
    >
      {icon}
      {children}
    </button>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-4 py-2 text-[13px] font-semibold break-all text-ink">{children}</div>
}

export function MenuDivider() {
  return <div className="my-1 h-px bg-line" />
}
